import { store } from "./stores"
import { Node as XYFlowNode } from "@xyflow/react"
import { UseFormReturnType } from "@mantine/form"
import { NodeDefinition, Property, Scenario, ScenarioNode, scenarioUpdateSchema } from "@/gen"
import { createStaticStore, StaticStore } from "@/lib/store-factory"
import { BaseNodeType } from "@/pages/Scenarios/components/nodes/Base.node"
import { endpoints } from "@/config/endpoints.config"
import { queryClient } from "@/lib/query-client"
import { client } from "@/hooks/useClient"
import { defaultScenarioActions } from "@/pages/Scenarios/actions/actions.def"
import { mergeTriggerNodes } from "@/pages/Scenarios/scenario.helper"
import { Node } from "@/nodes"

export const zScenarioUpdate = scenarioUpdateSchema
export type ScenarioNodeData = Omit<ScenarioNode, "position" | "id" | "type" | "targets"> & {
  node_type: string
}

// ███████╗ ██████╗██╗  ██╗███████╗███╗   ███╗ █████╗
// ██╔════╝██╔════╝██║  ██║██╔════╝████╗ ████║██╔══██╗
// ███████╗██║     ███████║█████╗  ██╔████╔██║███████║
// ╚════██║██║     ██╔══██║██╔══╝  ██║╚██╔╝██║██╔══██║
// ███████║╚██████╗██║  ██║███████╗██║ ╚═╝ ██║██║  ██║
// ╚══════╝ ╚═════╝╚═╝  ╚═╝╚══════╝╚═╝     ╚═╝╚═╝  ╚═╝
//
// export const ScenarioSchema = NamedEntitySchema.extend({
//   nodes: z.array(
//     z.object({
//       id: z.string(),
//       // type: z.nativeEnum(ActionTypeId),
//       type: z.enum(["base"]),
//       data: z.object({}),
//       // data: z.object({
//       //   label: z.string(),
//       //   node_type: z.nativeEnum(ActionTypeId),
//       // }),
//       position: z.object({
//         x: z.number(),
//         y: z.number(),
//       }),
//     }),
//   ),
//   edges: z.array(z.object({ id: z.string(), source: z.string(), target: z.string(), animated: z.boolean() })),
//   last_node_id: z.number().default(0),
// })

// export const ScenarioDataSchema = z.object({
//   label: z.string(),
//   node_type: z.nativeEnum(ActionTypeId),
//   invalid_tip: z.string().optional(),
//   properties: z.object({}).optional(),
//   meta_properties: z.record(
//     z.string(), 
//     z.object({
//       mode: z.enum(["expression", "fixed"])
//     })
//   ).optional(),
//   retry_on_fail: z.boolean().default(false),
//   max_retries: z.number().min(1).max(5).default(3),
//   retry_interval: z.number().min(100).max(5000).default(1000),
//   on_error: z.enum(["stop", "continue", "fail_branch"]).default("stop"),
// })

// export type ScenarioActionData = z.infer<typeof ScenarioDataSchema>

// export type Scenario = z.infer<typeof ScenarioSchema>

// ███████╗████████╗ ██████╗ ██████╗ ███████╗
// ██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗██╔════╝
// ███████╗   ██║   ██║   ██║██████╔╝█████╗
// ╚════██║   ██║   ██║   ██║██╔══██╗██╔══╝
// ███████║   ██║   ╚██████╔╝██║  ██║███████╗
// ╚══════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝╚══════╝
//
export interface ScenarioStore extends StaticStore<Scenario> {
  editing: {
    selectedNode?: { node_type: string; node?: XYFlowNode<ScenarioNodeData & Record<string, unknown>, any> }
    draggingNode?: boolean
    deletedNodes?: Record<string, boolean> | null
    highlightNode?: string | null
    duplicatedNodes?: BaseNodeType[] | null
    hoveringEdge?: boolean
    form?: UseFormReturnType<Zod.infer<typeof zScenarioUpdate>>
  }
  nodeDefinitions: {
    list: NodeDefinition[],
    record: Record<string, NodeDefinition>,
  }

  // Actions
  actions: {
    reloadNodes: () => Promise<void>
    setSelectedNode: (dto: { node_type: string; node?: XYFlowNode<ScenarioNodeData & Record<string, unknown>, any> } | null) => void
    setDeletedNodes: (nodes: Record<string, boolean> | null) => void
    setHighlightNode: (nodeId: string) => void
    resetEditing: () => void
    duplicateNodes: (nodes: BaseNodeType[]) => void
    getNodeDefinition: () => Promise<NodeDefinition[]>
    runScenario: (dto: { scenarioId: string, nodeId: string; version?: Scenario }) => Promise<any>
    getNodeDynamicProperties: (dto: {
      node_type: string
      action_id: string
      properties: Record<string, any>
      depends_on: string[]
    }) => Promise<{ properties: Property[]; optional_properties: Property[] }>
  } & StaticStore<Scenario>["actions"]
}

export const scenarioStore: ScenarioStore = createStaticStore<Scenario, any, any>(
  {
    endpoint: endpoints.SCENARIOS,
    selectedNodeId: null,
    draggingNode: false,
    editing: {},
    nodeDefinitions: {
      list: [],
      record: {},
    },
  },
  {
    getNodeDefinition: () => {
      return queryClient
        .fetchQuery({
          queryKey: [endpoints.NODES],
          queryFn: () => client.get<Node[]>(endpoints.NODES),
          staleTime: Infinity,
        })
        .then((res) => {
          const allNodeDefinitions = [...res.data, ...defaultScenarioActions]
          store.scenario.nodeDefinitions = {
            list: allNodeDefinitions,
            record: allNodeDefinitions.reduce((acc, item) => {
              acc[item.type] = item
              return acc
            }, {} as Record<string, NodeDefinition>),
          }

          store.credential.actions.setCredentialsMetaFromNodes(res.data)
        })
    },
    getNodeDynamicProperties: (dto: {
      node_type: string
      action_id: string
      properties: Record<string, any>
      depends_on: string[]
    }) => {
      const endpoint = `${endpoints.NODES}/${dto.node_type}/search-dynamic-properties`
      const { depends_on, ...payload } = dto
      return queryClient.fetchQuery({
        queryKey: [endpoint, dto.action_id, ...dto.depends_on || [], ...Object.entries(dto.properties).map(([key, value]) => `${key}:${value}`).join(",")],
        queryFn: () => client.post<{ properties: Property[]; optional_properties: Property[] }>(endpoint, payload).then((res) => res.data),
        staleTime: Infinity,
      })
    },
    runScenario: (dto: { scenarioId: string, nodeId: string; version?: Scenario }) => {
      return queryClient.fetchQuery({
        queryKey: ["run-scenario"],
        queryFn: () => client.post(`${endpoints.SCENARIOS}/${dto.scenarioId}/run`, {
          node_id: dto.nodeId,
          version: dto.version,
        }),
      })
    },
    reloadNodes: () => {
      return client.post(`${endpoints.NODES}/pull`, {})
    },
    resetEditing: () => {
      store.scenario.editing = {}
    },
    setSelectedNode: (dto) => {
      store.scenario.editing.selectedNode = dto
    },
    setDeletedNodes: (nodes) => {
      store.scenario.editing.deletedNodes = nodes
    },
    duplicateNodes: (nodes) => {
      store.scenario.editing.duplicatedNodes = nodes
    },
    setHighlightNode: (nodeId) => {
      setTimeout(() => {
        store.scenario.editing.highlightNode = nodeId
        setTimeout(() => {
          store.scenario.editing.highlightNode = null
        }, 500)
      }, 300)
    },
  },
)
